import React from 'react';
import { View, Text } from 'react-native';
import Svg, { Path, Circle, Text as SvgText, Line } from 'react-native-svg';
import { ChartConfig } from '../types';

interface LineChartProps {
  data: {
    labels: string[];
    datasets: Array<{
      data: number[];
      color: any;
      strokeWidth?: number;
      axis?: 'left' | 'right';
    }>;
  };
  width: number;
  height: number;
  chartConfig: ChartConfig;
  withDots?: boolean;
  withShadow?: boolean;
  withScrollableDot?: boolean;
  bezier?: boolean;
  isZeroValues?: boolean;
  maxValue?: number | null;
  hasFilterControls?: boolean;
  style?: any;
  periodFilter?: 'weekly' | 'monthly';
  children?: any;
  setChartValue?: (value: string | number) => void;
}

const LineChart: React.FC<LineChartProps> = ({
  data,
  width,
  height,
  chartConfig,
  withDots = true,
  bezier = false,
  isZeroValues = false,
  maxValue = null,
  hasFilterControls = false,
  style,
  periodFilter,
  children,
  setChartValue,
}) => {
  const hasValidData =
    data &&
    data.datasets &&
    data.datasets.length > 0 &&
    data.datasets.some((dataset) => dataset.data && dataset.data.length > 0);

  if (!hasValidData) {
    const chartMode = chartConfig.mode || 'single';

    // Generate mock data values based on maxValue
    const generateMockValues = (baseRange: number) => {
      const effectiveMax = maxValue && maxValue > 0 ? maxValue : baseRange;
      const minVal = Math.max(0, effectiveMax * 0.3); // 30% of max as minimum
      const maxVal = effectiveMax * 0.9; // 90% of max as maximum

      return Array.from({ length: 7 }, () =>
        Math.round(minVal + Math.random() * (maxVal - minVal))
      );
    };

    const mockData = {
      labels: ['5/21', '5/22', '5/23', '5/24', '5/25', '5/26', '5/27'],
      datasets:
        chartMode === 'multiple'
          ? [
              {
                data: generateMockValues(150), // First dataset
                color: '#007AFF',
                strokeWidth: 2,
                axis: 'left' as 'left',
                name: 'Series 1',
              },
              {
                data: generateMockValues(120), // Second dataset (lower range)
                color: '#FF3B30',
                strokeWidth: 2,
                axis: 'left' as 'left',
                name: 'Series 2',
              },
              {
                data: generateMockValues(100), // Third dataset (right axis)
                color: '#FFCC00',
                strokeWidth: 2,
                axis: 'right' as 'right',
                name: 'Series 3',
              },
              {
                data: generateMockValues(80), // Fourth dataset (right axis)
                color: '#34C759',
                strokeWidth: 2,
                axis: 'right' as 'right',
                name: 'Series 4',
              },
            ]
          : [
              {
                data: generateMockValues(150), // Single dataset
                color: '#007AFF',
                strokeWidth: 2,
                name: 'Data Series',
              },
            ],
    };

    data = mockData;
  }

  // Chart mode (single, multiple, or multiplot)
  const chartMode: string = chartConfig.mode || 'single';
  const isMultiPlotMode = chartMode === 'multiplot';

  // Fixed padding system to prevent layout overflow
  const hasAxisLabels = chartConfig.axisLabels?.x?.enabled !== false; // Default to true
  const hasLegend = chartConfig.legend?.enabled !== false; // Default to true
  // const isBottomLegend = chartConfig.legend?.position !== 'top'; // Unused after refactoring
  const legendHeight = hasLegend
    ? height < 250
      ? 25
      : height < 300
      ? 30
      : chartConfig.legend?.style?.height || 40
    : 0;

  // Calculate proper dimensions without overflow - reduce padding for smaller heights
  const basePaddingTop = height < 300 ? 20 : 30;
  // Bottom padding reduced since X-axis label space is already reserved by parent
  const basePaddingBottom = height < 300 ? 15 : 20;
  const basePaddingLeft = 60;
  const basePaddingRight = 60;

  const paddingTop = chartConfig.paddingTop || basePaddingTop;
  const paddingBottom = chartConfig.paddingBottom || basePaddingBottom;
  const paddingLeft = chartConfig.paddingLeft || basePaddingLeft;
  const paddingRight = chartConfig.paddingRight || basePaddingRight;

  // Ensure chart fits within the given dimensions
  const availableWidth = Math.max(width - paddingLeft - paddingRight, 100);

  const chartWidth = availableWidth;
  const shouldShowAxisLabels = isMultiPlotMode || hasAxisLabels;
  const xAxisLabelSpace = shouldShowAxisLabels ? (height < 300 ? 35 : 45) : 0;
  const availableSvgHeight = height - (hasLegend ? legendHeight : 0);
  const minRequiredSvgHeight =
    paddingTop + paddingBottom + xAxisLabelSpace + 80; // Minimum space needed
  const adjustedSvgHeight = Math.max(availableSvgHeight, minRequiredSvgHeight);
  const chartHeight =
    Math.max(
      adjustedSvgHeight - paddingTop - paddingBottom - xAxisLabelSpace,
      80
    ) - (hasFilterControls ? 50 : 0);

  // Process datasets based on mode (single shows only first dataset, multiple shows all)
  const datasetsToProcess =
    chartMode === 'single' ? [data.datasets[0]] : data.datasets;
  const processedDatasets = datasetsToProcess.map((dataset) => {
    const validData = dataset.data.map((val) =>
      typeof val === 'number' ? val : 0
    );
    return { ...dataset, validData };
  });

  // Get labels with date range filtering if enabled
  let filteredLabels =
    data.labels ||
    processedDatasets[0]?.validData.map((_, index) => `Item ${index + 1}`) ||
    [];
  let filteredDatasets = processedDatasets;

  if (
    chartConfig.dateRange?.enabled &&
    chartConfig.dateRange.startDate &&
    chartConfig.dateRange.endDate
  ) {
    const startDate = new Date(chartConfig.dateRange.startDate);
    const endDate = new Date(chartConfig.dateRange.endDate);

    const filteredIndices = filteredLabels
      .map((label, index) => {
        const labelDate = new Date(label);
        return labelDate >= startDate && labelDate <= endDate ? index : -1;
      })
      .filter((index) => index !== -1);

    if (filteredIndices.length > 0) {
      filteredLabels = filteredIndices.map((i) => filteredLabels[i]);
      filteredDatasets = processedDatasets.map((dataset) => ({
        ...dataset,
        validData: filteredIndices.map((i) => dataset.validData[i]),
      }));
    }
  }

  // Calculate value ranges for each axis
  const leftAxisData = filteredDatasets
    .filter((d) => !d.axis || d.axis === 'left')
    .flatMap((d) => d.validData);
  const rightAxisData = filteredDatasets
    .filter((d) => d.axis === 'right')
    .flatMap((d) => d.validData);

  const heightBasedAllowance = height
    ? Math.max(50, Math.min(200, Math.floor(height / 3)))
    : 100;

  // Use maxValue if provided, otherwise use data-based calculation
  const leftMaxValue =
    maxValue && maxValue > 0
      ? maxValue
      : leftAxisData.length > 0
      ? Math.max(...leftAxisData) + heightBasedAllowance
      : heightBasedAllowance;

  const leftMinValue =
    leftAxisData.length > 0 ? Math.min(0, Math.min(...leftAxisData)) : 0;
  const leftValueRange = leftMaxValue - leftMinValue || 1;

  // Use maxValue for right axis as well if provided
  const rightMaxValue =
    maxValue && maxValue > 0
      ? maxValue
      : rightAxisData.length > 0
      ? Math.max(...rightAxisData) + heightBasedAllowance
      : heightBasedAllowance;

  const rightMinValue =
    rightAxisData.length > 0 ? Math.min(0, Math.min(...rightAxisData)) : 0;
  const rightValueRange = rightMaxValue - rightMinValue || 1;

  const allDatasetPoints = filteredDatasets.map((dataset, datasetIndex) => {
    const isRightAxis = dataset.axis === 'right';
    const minVal = isRightAxis ? rightMinValue : leftMinValue;
    const valueRange = isRightAxis ? rightValueRange : leftValueRange;

    // Count valid (non-zero, non-null) values
    const validValues = dataset.validData.filter(
      (val) => val !== 0 && val !== null && val !== undefined
    );

    // If only one valid value exists, plot just that point
    if (validValues.length === 1) {
      const validIndex = dataset.validData.findIndex(
        (val) => val === validValues[0]
      );
      const x =
        paddingLeft +
        (validIndex / Math.max(dataset.validData.length - 1, 1)) * chartWidth;
      const y =
        paddingTop +
        chartHeight -
        ((validValues[0] - minVal) / valueRange) * chartHeight;
      return [
        {
          x,
          y,
          value: validValues[0],
          index: validIndex,
          datasetIndex,
          axis: dataset.axis || 'left',
        },
      ];
    }

    // Otherwise plot all points normally
    return dataset.validData.map((value, index) => {
      const x =
        paddingLeft +
        (index / Math.max(dataset.validData.length - 1, 1)) * chartWidth;
      const y =
        paddingTop +
        chartHeight -
        ((value - minVal) / valueRange) * chartHeight;
      return { x, y, value, index, datasetIndex, axis: dataset.axis || 'left' };
    });
  });

  // Generate path data for each dataset
  const pathDataArray = allDatasetPoints.map((points, datasetIndex) => {
    if (points.length === 0) return '';
    const dataset = filteredDatasets[datasetIndex];
    const hasNonZeroValues = dataset.validData.some((value) => value !== 0);
    if (!hasNonZeroValues) return '';

    // Filter out zero values for line connection
    const nonZeroPoints = points.filter((point) => point.value !== 0);
    if (nonZeroPoints.length === 0) return '';

    return nonZeroPoints
      .map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`)
      .join(' ');
  });

  // Enhanced grid system - use maxValue if provided, otherwise responsive to height
  const responsiveGridCount = height
    ? Math.min(10, Math.max(3, Math.floor(height / 60)))
    : 5;

  // Calculate gridCount based on maxValue when available
  const maxValueBasedGridCount =
    maxValue && maxValue > 0
      ? Math.min(20, Math.max(5, Math.ceil(maxValue / 20)))
      : null;

  // Priority: chartConfig.gridCount > maxValue-based > responsive
  const gridCount =
    chartConfig.gridCount !== undefined && chartConfig.gridCount !== null
      ? chartConfig.gridCount
      : maxValueBasedGridCount !== null
      ? maxValueBasedGridCount
      : responsiveGridCount;

  // Use horizontalGridCount for Y-axis grid (legends) if provided, otherwise use gridCount
  const horizontalGridCount =
    chartConfig.horizontalGridCount !== undefined &&
    chartConfig.horizontalGridCount !== null
      ? chartConfig.horizontalGridCount
      : gridCount;

  const horizontalGridRatios = Array.from(
    { length: horizontalGridCount },
    (_, i) => i / (horizontalGridCount - 1)
  );
  const showHorizontalGrid = chartConfig.showHorizontalGrid !== false;
  const showVerticalGrid = chartConfig.showVerticalGrid || false;

  // Axis labels with mock data fallback - always show labels with mock data if no config
  const getAxisLabel = (axisConfig: any, mockLabel: string) => {
    // If axisConfig is undefined, show mock data by default
    if (!axisConfig) return mockLabel;
    // If explicitly disabled, return null
    if (axisConfig.enabled === false) return null;

    // Handle CURRENT_USER_SELECTOR binding labels
    if (axisConfig.label && Array.isArray(axisConfig.label)) {
      // Extract binding object from array structure like ["", {binding}, ""]
      const bindingObject = axisConfig.label.find(
        (item: any) => typeof item === 'object' && item.source
      );
      if (
        bindingObject &&
        bindingObject.source?.source?.selector?.type === 'CURRENT_USER_SELECTOR'
      ) {
        // For current user selector, show user-friendly label
        return bindingObject.label || 'Authenticated User';
      }
    }

    // Otherwise return the label or mock data, ensuring it's a string
    const label = axisConfig.label || mockLabel;
    return typeof label === 'string' ? label : String(label);
  };

  // Use resolved axis labels from server if available (for CURRENT_USER_SELECTOR bindings)
  const resolvedAxisLabels = (data as any)?.resolvedAxisLabels;

  const xAxisLabel = shouldShowAxisLabels
    ? resolvedAxisLabels?.x ||
      getAxisLabel(
        chartConfig.axisLabels?.x,
        isMultiPlotMode ? 'X-Axis' : 'Time Period'
      )
    : null;
  const yAxisLabel = shouldShowAxisLabels
    ? resolvedAxisLabels?.y ||
      getAxisLabel(
        chartConfig.axisLabels?.y,
        isMultiPlotMode
          ? 'Y-Axis'
          : chartMode === 'single'
          ? 'Y'
          : 'Primary Values'
      )
    : null;
  const yRightAxisLabel = chartConfig.multiAxis?.enabled
    ? getAxisLabel(chartConfig.axisLabels?.yRight, 'Secondary Values')
    : null;

  // Legend with dynamic data from datasets
  const getLegendItems = () => {
    if (chartConfig.legend?.items && chartConfig.legend.items.length > 0) {
      return chartConfig.legend.items;
    }

    if (filteredDatasets && filteredDatasets.length > 0) {
      return filteredDatasets.map((dataset, index) => ({
        label: (dataset as any).name || `Dataset ${index + 1}`,
        color: dataset.color || chartConfig.color?.(1) || '#0175ff',
        dotSize: 8,
        fontSize: 12,
      }));
    }

    return [
      {
        label: 'Data Series',
        color: chartConfig.color?.(1) || '#0175ff',
        dotSize: 8,
        fontSize: 12,
      },
    ];
  };

  const legendItems = hasLegend ? getLegendItems() : [];

  // Render Legend Component
  const renderLegend = () => {
    if (!hasLegend || legendItems.length === 0) return null;

    const legendStyle = chartConfig.legend?.style || {};

    return (
      <View
        style={{
          backgroundColor: legendStyle.backgroundColor || 'transparent',
          minHeight: legendHeight,
          paddingHorizontal: legendStyle.padding || 16,
          paddingVertical: 0,
          flexDirection: 'row',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          // Remove absolute positioning - let it flow naturally
        }}
      >
        {legendItems.map((item, index) => (
          <View
            key={index}
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              marginRight: 12, // Space between items
              marginBottom: 0, // Space between wrapped lines
              maxWidth: '100%', // Prevent overflow
            }}
          >
            <View
              style={{
                width: item.dotSize || 8,
                height: item.dotSize || 8,
                borderRadius: (item.dotSize || 8) / 2,
                backgroundColor: item.color,
                marginRight: 6,
              }}
            />
            <Text
              numberOfLines={1} // Single line with ellipsis
              ellipsizeMode="tail"
              style={{
                fontSize: (item as any).fontSize || 12,
                color: item.color,
                flexShrink: 1, // Allow text to shrink
              }}
            >
              {typeof item.label === 'string' ? item.label : String(item.label)}
            </Text>
          </View>
        ))}
      </View>
    );
  };

  const isInSvgContext =
    typeof style?.x === 'number' || typeof style?.y === 'number';

  const sumValue = React.useMemo(() => {
    return data.datasets[0].data.reduce((sum, item) => sum + item, 0) || 0;
  }, [data]);

  React.useEffect(() => {
    if (setChartValue) {
      setChartValue(sumValue);
    }
  }, [sumValue]);

  return (
    <View
      style={[
        {
          paddingHorizontal:
            chartConfig.container?.padding || (height < 300 ? 4 : 8),
          // Only add top padding if filters are present OR legend is at top
          paddingTop:
            hasFilterControls || chartConfig.legend?.position === 'top'
              ? chartConfig.container?.padding || (height < 300 ? 4 : 8)
              : 0,
          borderRadius: chartConfig.container?.borderRadius || 0,
          overflow: 'hidden', // Ensure content fits within bounds
          height: height, // Enforce exact height
          maxHeight: height, // Additional constraint to prevent overflow
        },
        style,
      ]}
    >
      <View
        style={{
          backgroundColor: 'transparent',
          overflow: 'visible',
          ...(isInSvgContext
            ? { height: '100%' } // Use explicit height for SVG context
            : { flex: 1 }),
        }}
      >
        {children}
        {/* Top legend for both contexts */}
        {hasLegend && chartConfig.legend?.position === 'top' && (
          <View style={isInSvgContext ? { height: legendHeight } : {}}>
            {renderLegend()}
          </View>
        )}

        {/* Chart container */}
        <View style={isInSvgContext ? {} : { flex: 1 }}>
          <Svg
            width={width}
            height={Math.min(adjustedSvgHeight, availableSvgHeight)}
          >
            {/* Enhanced horizontal grid lines */}
            {showHorizontalGrid &&
              horizontalGridRatios.map((ratio, index) => (
                <Line
                  key={`h-grid-${index}`}
                  x1={paddingLeft}
                  y1={paddingTop + chartHeight * ratio}
                  x2={paddingLeft + chartWidth}
                  y2={paddingTop + chartHeight * ratio}
                  stroke={
                    chartConfig.propsForBackgroundLines?.stroke || '#e3e3e3'
                  }
                  strokeWidth={
                    chartConfig.propsForBackgroundLines?.strokeWidth || 1
                  }
                  strokeDasharray={
                    chartConfig.propsForBackgroundLines?.strokeDasharray
                  }
                />
              ))}

            {/* Reference lines (dashed horizontal lines) */}
            {chartConfig.referenceLines?.map((refLine, index) => {
              const isRightAxis = refLine.axis === 'right';
              const minVal = isRightAxis ? rightMinValue : leftMinValue;
              const valueRange = isRightAxis ? rightValueRange : leftValueRange;

              const y =
                paddingTop +
                chartHeight -
                ((refLine.value - minVal) / valueRange) * chartHeight;

              return (
                <React.Fragment key={`ref-line-${index}`}>
                  <Line
                    x1={paddingLeft}
                    y1={y}
                    x2={paddingLeft + chartWidth}
                    y2={y}
                    stroke={refLine.color || '#666'}
                    strokeWidth={refLine.strokeWidth || 1}
                    strokeDasharray={refLine.strokeDasharray || '5,5'}
                  />
                  {refLine.label && (
                    <SvgText
                      x={
                        refLine.labelPosition === 'right'
                          ? paddingLeft + chartWidth - 5
                          : paddingLeft + 5
                      }
                      y={y - 5}
                      textAnchor={
                        refLine.labelPosition === 'right' ? 'end' : 'start'
                      }
                      fontSize="10"
                      fill={refLine.color || '#666'}
                    >
                      {refLine.label}
                    </SvgText>
                  )}
                </React.Fragment>
              );
            })}

            {showVerticalGrid &&
              filteredLabels.map((_, index) => {
                const x =
                  paddingLeft +
                  (index / Math.max(filteredLabels.length - 1, 1)) * chartWidth;
                return (
                  <Line
                    key={`v-grid-${index}`}
                    x1={x}
                    y1={paddingTop}
                    x2={x}
                    y2={paddingTop + chartHeight}
                    stroke={
                      chartConfig.propsForBackgroundLines?.stroke || '#e3e3e3'
                    }
                    strokeWidth={
                      chartConfig.propsForBackgroundLines?.strokeWidth || 1
                    }
                    strokeDasharray={
                      chartConfig.propsForBackgroundLines?.strokeDasharray
                    }
                  />
                );
              })}

            {/* Line paths for all datasets */}
            {pathDataArray.map((pathData, datasetIndex) => {
              const dataset = filteredDatasets[datasetIndex];
              return pathData ? (
                <Path
                  key={`path-${datasetIndex}`}
                  d={pathData}
                  stroke={dataset.color || chartConfig.color?.(1) || '#0175ff'}
                  strokeWidth={
                    dataset.strokeWidth || chartConfig.strokeWidth || 2
                  }
                  fill="none"
                />
              ) : null;
            })}

            {withDots &&
              allDatasetPoints.map((points, datasetIndex) => {
                const dataset = filteredDatasets[datasetIndex];
                return points.map((point, pointIndex) =>
                  point.value !== 0 ? (
                    <Circle
                      key={`dot-${datasetIndex}-${pointIndex}`}
                      cx={point.x}
                      cy={point.y}
                      r={parseInt(chartConfig.propsForDots?.r || '4')}
                      fill={
                        chartConfig.propsForDots?.fill ||
                        dataset.color ||
                        chartConfig.color?.(1) ||
                        '#0175ff'
                      }
                    />
                  ) : null
                );
              })}

            {/* Y-axis labels (left) */}
            {horizontalGridRatios.map((ratio, index) => {
              const value =
                leftMinValue + (leftMaxValue - leftMinValue) * (1 - ratio);
              const leftFormatFn = chartConfig.multiAxis?.leftAxis?.format;
              const formatFn =
                leftFormatFn ||
                ((v: number) =>
                  chartConfig.formatYLabel
                    ? chartConfig.formatYLabel(v.toString())
                    : Math.round(v).toString());
              return (
                <SvgText
                  key={`left-y-${index}`}
                  x={paddingLeft - 10}
                  y={paddingTop + chartHeight * ratio + 4}
                  textAnchor="end"
                  fontSize="12"
                  fill={chartConfig.color?.(0.8) || '#666'}
                >
                  {formatFn(Number(value.toFixed(0)))}
                </SvgText>
              );
            })}

            {/* Y-axis labels (right) */}
            {chartConfig.multiAxis?.enabled &&
              rightAxisData.length > 0 &&
              horizontalGridRatios.map((ratio, index) => {
                const value =
                  rightMinValue + (rightMaxValue - rightMinValue) * (1 - ratio);
                const rightFormatFn = chartConfig.multiAxis?.rightAxis?.format;
                const formatFn =
                  rightFormatFn ||
                  ((v: number) =>
                    chartConfig.formatYLabel
                      ? chartConfig.formatYLabel(v.toString())
                      : v.toFixed(1));
                return (
                  <SvgText
                    key={`right-y-${index}`}
                    x={paddingLeft + chartWidth + 10}
                    y={paddingTop + chartHeight * ratio + 4}
                    textAnchor="start"
                    fontSize="12"
                    fill={chartConfig.color?.(0.8) || '#666'}
                  >
                    {formatFn(Number(value.toFixed(1)))}
                  </SvgText>
                );
              })}

            {/* Enhanced X-axis labels */}
            {filteredLabels.map((label, index) => {
              const x =
                paddingLeft +
                (index / Math.max(filteredLabels.length - 1, 1)) * chartWidth;
              const formatFn = chartConfig.formatXLabel || ((l) => l);
              const displayLabel = chartConfig.dateRange?.format
                ? new Date(label).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                  })
                : formatFn(label);

              return (
                <SvgText
                  key={`x-label-${index}`}
                  x={x}
                  y={paddingTop + chartHeight + 20}
                  textAnchor="middle"
                  fontSize="12"
                  fill={chartConfig.color?.(1) || '#333'}
                >
                  {displayLabel || ``}
                </SvgText>
              );
            })}

            {xAxisLabel && (
              <SvgText
                x={paddingLeft + chartWidth / 2}
                y={paddingTop + chartHeight + (height < 300 ? 35 : 45)}
                textAnchor="middle"
                fontSize={
                  chartConfig.axisLabels?.x?.fontSize ||
                  (height < 300 ? 10 : 12)
                }
                fill={
                  chartConfig.axisLabels?.x?.color ||
                  chartConfig.color?.(1) ||
                  '#666'
                }
                fontWeight="600"
              >
                {xAxisLabel}
              </SvgText>
            )}

            {/* Y-axis title (left) */}
            {yAxisLabel && (
              <SvgText
                x={chartConfig.axisLabels?.y?.offsetX || 15}
                y={paddingTop + chartHeight / 2}
                textAnchor="middle"
                fontSize={chartConfig.axisLabels?.y?.fontSize || 14}
                fill={
                  chartConfig.axisLabels?.y?.color ||
                  chartConfig.color?.(1) ||
                  '#666'
                }
                fontWeight="600"
                transform={`rotate(-90, ${
                  chartConfig.axisLabels?.y?.offsetX || 15
                }, ${paddingTop + chartHeight / 2})`}
              >
                {yAxisLabel}
              </SvgText>
            )}

            {/* Y-axis title (right) */}
            {yRightAxisLabel && chartConfig.multiAxis?.enabled && (
              <SvgText
                x={
                  paddingLeft +
                  chartWidth +
                  (chartConfig.axisLabels?.yRight?.offsetX || 35)
                }
                y={paddingTop + chartHeight / 2}
                textAnchor="middle"
                fontSize={chartConfig.axisLabels?.yRight?.fontSize || 14}
                fill={
                  chartConfig.axisLabels?.yRight?.color ||
                  chartConfig.color?.(1) ||
                  '#666'
                }
                fontWeight="600"
                transform={`translate(${
                  paddingLeft +
                  chartWidth +
                  (chartConfig.axisLabels?.yRight?.offsetX || 35)
                }, ${paddingTop + chartHeight / 2}) rotate(-90)`}
              >
                {yRightAxisLabel}
              </SvgText>
            )}
          </Svg>
        </View>

        {/* Bottom legend for both contexts */}
        {hasLegend && chartConfig.legend?.position !== 'top' && (
          <View style={isInSvgContext ? { height: legendHeight } : {}}>
            {renderLegend()}
          </View>
        )}
      </View>
    </View>
  );
};

export default LineChart;
